import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import {
  Room,
  RoomEvent,
  VideoPresets,
  LocalTrack,
  RemoteParticipant,
  ConnectionState,
  LocalParticipant,
  LocalTrackPublication,
  Track,
  Participant,
  RemoteTrack,
  TrackPublication,
  VideoQuality, // Import VideoQuality if needed here later
  DataPacket_Kind, // Import for Data Messages
  RemoteTrackPublication
} from "livekit-client";
// --- FIX: Correct import paths ---
import StreamPlayer from "./StreamPlayer.tsx"; // Use the component in the same directory
import IntroPlayer from "./IntroPlayer.tsx"; // Import IntroPlayer
import StreamControls from "./StreamControls.tsx"; // Use the component in the same directory
// --- END FIX ---
import { isStreamPaused, checkStreamForNSFW, serverConfig } from "../../utils/moderation.ts";
import { useAccount } from "wagmi";
import axios from 'axios';
import { apiUrl } from "../../utils/constants.ts";
import { FaSpinner, FaPlay, FaBroadcastTower, FaCalendarAlt, FaUser } from 'react-icons/fa'; // Add additional icons
import { ChatOverlay } from "./ChatOverlay.tsx";
import { ReactionOverlay } from "./ReactionOverlay.tsx";
import { JoinRequestButton } from "./JoinRequestButton.tsx";
// import the actual MP4 so bundler includes it
import introVideo from "../../assets/intro.mp4";

// --- Constants ---
const INTRO_VIDEO_SRC = introVideo;  // bundled asset import
const INTRO_DURATION_MS = 19950; // Replace with actual duration in milliseconds (e.g., 15 seconds)
// --- ---

interface StreamContainerProps {
  tokenAddress: string | undefined;
  connectedWallet: string | undefined;
  ownerAddress: string | undefined;
  setShowReportModal: React.Dispatch<React.SetStateAction<boolean>>;
  showReportModal: boolean;
  setIsPaused: React.Dispatch<React.SetStateAction<boolean>>;
  isPaused: boolean;
  includeChat?: boolean; // <-- Nueva prop opcional para incluir chat
}

type StreamPhase = 'idle' | 'intro' | 'live'; // Define stream phases

const StreamContainer = forwardRef<{ room: Room | null }, StreamContainerProps>(({ tokenAddress, connectedWallet, ownerAddress, setShowReportModal, showReportModal, setIsPaused, isPaused, includeChat = false }, ref) => {
  const { address } = useAccount();
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamUrl, setStreamUrl] = useState<string | null>(null); // Usado principalmente para indicar si *alguien* está streameando
  const [reported, setReported] = useState<boolean>(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Disconnected);
  const [isMicMuted, setIsMicMuted] = useState(true); // Start muted
  const [isCamOff, setIsCamOff] = useState(true); // Start off
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [remoteStreamerMicMuted, setRemoteStreamerMicMuted] = useState(false);
  const [remoteStreamerCamOff, setRemoteStreamerCamOff] = useState(false);
  const [isPreviewReady, setIsPreviewReady] = useState(false); // <-- Nuevo estado
  const [showPreview, setShowPreview] = useState<boolean>(false); // <-- NUEVO: Estado para controlar si mostrar la previsualización
  const [streamPhase, setStreamPhase] = useState<StreamPhase>('idle');
  const [introStartTime, setIntroStartTime] = useState<number | null>(null); // Timestamp when intro started

  // Calculate isOwner directly from props
  const isOwner = connectedWallet && ownerAddress ? connectedWallet.toLowerCase() === ownerAddress.toLowerCase() : false;

  const roomRef = useRef<Room | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const localVideoTrackRef = useRef<LocalTrackPublication | null>(null);
  const localAudioTrackRef = useRef<LocalTrackPublication | null>(null);
  const screenShareTrackRef = useRef<LocalTrackPublication | null>(null);
  const screenShareStreamRef = useRef<MediaStream | null>(null);
  const prevIsSharingScreenRef = useRef<boolean>(); // <-- Add ref to track previous screen share state

  // --- Refs for state values needed in stable callbacks ---
  const isOwnerRef = useRef(isOwner); // Initialize ref with calculated value
  const isStreamingRef = useRef(isStreaming);
  const isSharingScreenRef = useRef(isSharingScreen);
  const isPausedRef = useRef(isPaused); // Add ref for isPaused
  const streamPhaseRef = useRef(streamPhase); // Add ref for streamPhase
  const isMicMutedRef = useRef(isMicMuted); // Add ref for isMicMuted
  const isCamOffRef = useRef(isCamOff); // Add ref for isCamOff
  const showPreviewRef = useRef(showPreview); // <-- NUEVO: Referencia para el nuevo estado

  // Effect to keep refs updated
  useEffect(() => {
    isOwnerRef.current = isOwner; // Update ref based on calculated isOwner
    isStreamingRef.current = isStreaming;
    isSharingScreenRef.current = isSharingScreen;
    isPausedRef.current = isPaused; // Update isPaused ref
    streamPhaseRef.current = streamPhase; // Update streamPhase ref
    isMicMutedRef.current = isMicMuted; // Update isMicMuted ref
    isCamOffRef.current = isCamOff; // Update isCamOff ref
    showPreviewRef.current = showPreview; // <-- NUEVO: Actualizar la referencia
    // console.log(logPrefix(), "[Ref Update Effect] Refs updated."); // Optional: Keep commented unless debugging ref updates
  }, [isOwner, isStreaming, isSharingScreen, isPaused, streamPhase, isMicMuted, isCamOff, showPreview]); // Add isMicMuted, isCamOff dependencies here
  // --- ---

  // --- Stable Callbacks using Refs ---
  const logPrefix = useCallback(() => {
    // Read directly from ref, no dependency needed
    return isOwnerRef.current ? "[Owner]" : "[Viewer]";
  }, []); // Empty dependency array makes it stable

  // getUserMedia con constraints de alta resolución 1080p
  const getLocalPreviewStream = useCallback(async () => {
    // --- Reduce log verbosity ---
    // Log entry point is fine
    console.log(logPrefix(), `getLocalPreviewStream called.`);
    if (isOwnerRef.current && !localStreamRef.current && !isPausedRef.current) {
      // Solo log si se ha solicitado la previsualización
      if (showPreviewRef.current) {
        console.log(logPrefix(), "Getting local stream for preview...");
      }
      
      try {
        // Check if mediaDevices is available
        if (!navigator.mediaDevices) {
          console.error(logPrefix(), "navigator.mediaDevices is not available.");
          alert("Media devices are not available in this browser.");
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { min: 640, ideal: 1280, max: 1920 }, height: { min: 480, ideal: 720, max: 1080 } },
          audio: true
        });

        // Check if stream is valid
        if (!stream) {
          console.error(logPrefix(), "getUserMedia returned null or undefined stream.");
          alert("Could not get local media stream.");
          return;
        }

        localStreamRef.current = stream;
        // Update state based on actual tracks acquired and their enabled status
        const audioEnabled = stream.getAudioTracks().some(t => t.enabled);
        const videoEnabled = stream.getVideoTracks().some(t => t.enabled);
        setIsMicMuted(!audioEnabled);
        setIsCamOff(!videoEnabled);
        setIsPreviewReady(true); // <-- Establecer preview como lista
        // Log success is fine
        console.log(logPrefix(), `Local preview stream acquired. Preview Ready.`);
      } catch (error: any) {
        console.error(logPrefix(), "Error getting local media for preview:", error);
        if (error.name === 'NotFoundError') {
          alert("No se encontró cámara o micrófono. Por favor conecta y permite el acceso a tus dispositivos.");
        } else if (error.name === 'NotAllowedError') {
          alert("Permiso denegado. Por favor permite el acceso a la cámara y micrófono en tu navegador.");
        } else if (error.name === 'NotReadableError') {
            alert("La cámara o el micrófono están en uso por otra aplicación. Cierra la otra aplicación e intenta de nuevo.");
        }
        else {
          alert("No se pudo acceder a la cámara/micrófono: " + error.message);
        }
        // Ensure state reflects failure
        setIsMicMuted(true);
        setIsCamOff(true);
        setIsPreviewReady(false); // <-- Asegurar que preview no está lista
        setShowPreview(false); // <-- NUEVO: Resetear el estado de mostrar preview si falla
      }
    } else {
        // Log skip reason is fine
        // console.log(logPrefix(), `getLocalPreviewStream conditions not met or already have stream.`);
        // If stream already exists, mark preview as ready if it wasn't
        if (isOwnerRef.current && localStreamRef.current) {
            setIsPreviewReady(true);
        }
    }
  }, [logPrefix]); // Depends only on stable logPrefix

  const republishCamera = useCallback(async () => {
    const participant = roomRef.current?.localParticipant;
    const localVideo = localStreamRef.current?.getVideoTracks()[0];

    // --- Condition Check ---
    // Use the ref here, which should be updated by the time the useEffect triggers this
    if (participant && localVideo && !localVideoTrackRef.current && !isSharingScreenRef.current) {
      console.log(logPrefix(), "Republishing camera track...");
      try {
        localVideoTrackRef.current = await participant.publishTrack(localVideo, {
          simulcast: true,                // enable simulcast layers
          videoEncoding: { maxBitrate: 6_000_000, maxFramerate: 30 },
          source: Track.Source.Camera,
        });
        const enabled = participant.isCameraEnabled;
        setIsCamOff(!enabled); // Update state based on SDK
        setIsStreaming(true); // Ensure streaming state is true
        setStreamUrl(`livekit:${tokenAddress}`);
        console.log(logPrefix(), "Camera track republished successfully. SID:", localVideoTrackRef.current.trackSid, "Enabled:", enabled);
      } catch (publishError) {
        console.error(logPrefix(), "Error republishing camera:", publishError);
        localVideoTrackRef.current = null;
        // Optionally reset state if republish fails
        // setIsCamOff(true);
        // setIsStreaming(false);
      }
    } else {
      console.log(logPrefix(), "Conditions not met for republishing camera.", { hasParticipant: !!participant, hasLocalVideo: !!localVideo, videoAlreadyPublished: !!localVideoTrackRef.current, isSharingScreen: isSharingScreenRef.current });
    }
  }, [logPrefix, tokenAddress]); // Dependencies remain the same

  // Define stopScreenShare BEFORE startScreenShare
  const stopScreenShare = useCallback(async () => {
    if (!roomRef.current || !isSharingScreenRef.current || !screenShareTrackRef.current?.track) {
      console.warn(logPrefix(), "Stop Screen Share called but not sharing or no room/track.");
      return;
    }
    console.log(logPrefix(), "Attempting to stop screen share...");

    try {
      await roomRef.current.localParticipant.unpublishTrack(screenShareTrackRef.current.track);
      console.log(logPrefix(), "Screen share track unpublished.");

      if (screenShareStreamRef.current) {
        screenShareStreamRef.current.getTracks().forEach(track => track.stop());
        screenShareStreamRef.current = null;
        console.log(logPrefix(), "Screen share stream stopped.");
      }

      screenShareTrackRef.current = null;
      // Set state to trigger the useEffect for republishing
      setIsSharingScreen(false);
      setIsCamOff(false); // Assume camera should be on after stopping share

      // --- REMOVED direct call to republishCamera ---
      // console.log(logPrefix(), "Attempting to republish camera after stopping screen share.");
      // await republishCamera(); // <-- REMOVE THIS LINE

    } catch (error) {
      console.error(logPrefix(), "Error stopping screen share:", error);
      // Ensure state is reset even on error
      screenShareTrackRef.current = null;
      setIsSharingScreen(false);
      setIsCamOff(false); // Or based on previous state if needed
    }
  }, [logPrefix]); // Removed republishCamera dependency

  const startScreenShare = useCallback(async () => {
    if (!roomRef.current || isSharingScreenRef.current) {
      console.warn(logPrefix(), "Start Screen Share called but already sharing or no room.");
      return;
    }
    console.log(logPrefix(), "Attempting to start screen share...");

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      screenShareStreamRef.current = screenStream;
      const screenVideoTrack = screenStream.getVideoTracks()[0];
      console.log(logPrefix(), "Screen stream acquired.");

      if (localVideoTrackRef.current?.track) {
        console.log(logPrefix(), "Unpublishing camera track to switch to screen share.");
        await roomRef.current.localParticipant.unpublishTrack(localVideoTrackRef.current.track);
        localVideoTrackRef.current = null;
      }

      console.log(logPrefix(), "Publishing screen share track.");
      screenShareTrackRef.current = await roomRef.current.localParticipant.publishTrack(screenVideoTrack, {
        name: 'screen',
        simulcast: false,
        source: Track.Source.ScreenShare,
      });

      setIsSharingScreen(true);
      setIsCamOff(true);
      console.log(logPrefix(), "Screen share started successfully.");

      // --- MODIFIED onended handler ---
      screenVideoTrack.onended = async () => {
        console.log(logPrefix(), "[onended] Screen share stopped via browser control.");

        // --- Manual Cleanup ---
        // 1. Check if we are actually in a state where cleanup makes sense
        // Use the ref here to check the state *before* this event potentially changes it
        if (roomRef.current && isSharingScreenRef.current && screenShareTrackRef.current?.track) {
            const participant = roomRef.current.localParticipant;
            const screenTrack = screenShareTrackRef.current.track;
            console.log(logPrefix(), "[onended] Manually unpublishing screen track:", screenTrack.sid);
            try {
                // It's possible the SDK already unpublished it, but try anyway
                await participant.unpublishTrack(screenTrack, false); // `stopOnUnpublish = false` as we stop it below
                console.log(logPrefix(), "[onended] Screen track unpublished successfully (or was already unpublished).");
            } catch (unpublishError: any) {
                // Ignore "track not found" errors as it might already be unpublished
                if (!unpublishError.message?.includes('track not found')) {
                    console.error(logPrefix(), "[onended] Error unpublishing screen track:", unpublishError);
                } else {
                    console.log(logPrefix(), "[onended] Screen track likely already unpublished by SDK.");
                }
            }
        } else {
            console.warn(logPrefix(), "[onended] Conditions not met for manual unpublish (ref indicates not sharing or track ref missing).");
        }

        // 2. Stop local screen stream tracks
        if (screenShareStreamRef.current) {
            console.log(logPrefix(), "[onended] Stopping local screen stream tracks.");
            screenShareStreamRef.current.getTracks().forEach(track => track.stop());
            screenShareStreamRef.current = null;
        }

        // 3. Reset state refs and trigger React state update
        // Important: Update refs *before* setting state if other logic relies on refs being immediately correct
        screenShareTrackRef.current = null;
        isSharingScreenRef.current = false; // Update ref immediately

        // Set React state to trigger the useEffect hook that handles republishing
        console.log(logPrefix(), "[onended] Setting React state: isSharingScreen=false, isCamOff=false");
        setIsSharingScreen(false);
        // We expect camera to come back on, set the state optimistically
        // The useEffect will handle the actual publishing/enabling
        setIsCamOff(false);

        // --- REMOVED direct call to republishCamera ---
        // console.log(logPrefix(), "[onended] Relying on useEffect to republish camera.");
        // await republishCamera(); // DO NOT CALL HERE

      };
      // --- END MODIFIED onended handler ---

    } catch (error) {
      console.error(logPrefix(), "Error starting screen share:", error);
      if (screenShareStreamRef.current) {
        screenShareStreamRef.current.getTracks().forEach(track => track.stop());
        screenShareStreamRef.current = null;
      }
      setIsSharingScreen(false);
      setIsCamOff(false);
      console.log(logPrefix(), "Attempting to republish camera after screen share failure.");
      await republishCamera();
    }
  }, [logPrefix, republishCamera]); // Keep stopScreenShare dependency, remove republishCamera

  const toggleMic = useCallback(async () => {
    const participant = roomRef.current?.localParticipant;
    const audioPublication = localAudioTrackRef.current; // Get the publication ref

    // --- Add Detailed Check ---
    if (!participant) {
        console.warn(logPrefix(), "Toggle Mic failed: Local participant not found.");
        return;
    }
    // Check the publication ref directly
    if (!audioPublication || !audioPublication.track) {
        console.warn(logPrefix(), "Toggle Mic failed: Local audio publication or track not found in ref.");
        // Log current publications for debugging
        console.log(logPrefix(), "Current audio publications:", Array.from(participant.audioTrackPublications.values()).map(p => ({ sid: p.trackSid, muted: p.isMuted, track: !!p.track })));
        return;
    }
    // --- ---

    // --- MODIFIED LOGIC ---
    // Determine the desired *next* state based on the current React state
    const shouldEnable = isMicMuted; // If React state says it's muted (true), we want to enable (true). If unmuted (false), we want to disable (false).

    // Log the SDK state from the publication for comparison
    const sdkMutedState = audioPublication.isMuted; // Read from publication, might be more reliable
    console.log(logPrefix(), `[toggleMic Pre-Check] React State isMicMuted: ${isMicMuted}, SDK Pub isMuted: ${sdkMutedState}, Calculated shouldEnable: ${shouldEnable}`);
    // --- END MODIFIED LOGIC ---

    console.log(logPrefix(), shouldEnable ? "Attempting to UNMUTE Mic" : "Attempting to MUTE Mic");
    try {
      const enableMic = !!shouldEnable; // This will be true if isMicMuted is true, false otherwise
      console.log(logPrefix(), `Calling participant.setMicrophoneEnabled with value: ${enableMic}`);
      await participant.setMicrophoneEnabled(enableMic);
      // Log SDK state *after* the call completes (using the publication)
      const newSdkMutedState = localAudioTrackRef.current?.isMuted; // Re-check ref
      console.log(logPrefix(), `Microphone setMicrophoneEnabled(${enableMic}) finished. New SDK Pub state isMuted: ${newSdkMutedState}`);
      // Update React state based on the *intended* new state
      setIsMicMuted(!enableMic); // Correctly toggles the React state
    } catch (error) {
      console.error(logPrefix(), "Error toggling mic:", error);
      // Optionally revert React state if the call failed
      // setIsMicMuted(isMicMuted); // Revert to the state before the attempt
    }
  }, [logPrefix, isMicMuted]); // Depend on logPrefix and the React state isMicMuted

  const toggleCam = useCallback(async () => {
    const participant = roomRef.current?.localParticipant;
    const videoTrack = localVideoTrackRef.current?.track;
    if (isSharingScreenRef.current) {
      console.warn(logPrefix(), "Cannot toggle camera while screen sharing.");
      return;
    }

    if (participant && videoTrack) {
      const currentCamState = participant.isCameraEnabled;
      const shouldEnable = !currentCamState;
      console.log(logPrefix(), shouldEnable ? "Starting Cam" : "Stopping Cam", "(Current SDK state:", currentCamState, ")");
      try {
        await participant.setCameraEnabled(shouldEnable);
        setIsCamOff(!shouldEnable);
        console.log(logPrefix(), "Camera enabled state set to:", shouldEnable);
      } catch (error) {
        console.error(logPrefix(), "Error toggling cam:", error);
      }
    } else {
      console.warn(logPrefix(), "Toggle Cam called but no local participant or video track published, or screen sharing.");
    }
  }, [logPrefix]); // Removed isSharingScreen state dependency

  // --- NEW: Function to publish actual live tracks ---
  const publishLiveTracks = useCallback(async () => {
    console.log(logPrefix(), "publishLiveTracks: Attempting to publish live media.");
    if (!isOwnerRef.current || !roomRef.current || !localStreamRef.current) {
      console.warn(logPrefix(), "publishLiveTracks: Conditions not met (not owner, no room, or no local stream).");
      setStreamPhase('idle'); // Revert phase if publishing fails early
      return;
    }

    const participant = roomRef.current.localParticipant;

    try {
      console.log(logPrefix(), "Publishing existing local tracks for LIVE stream...");
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      const audioTrack = localStreamRef.current.getAudioTracks()[0];

      // Publish Video (if needed)
      if (videoTrack && !localVideoTrackRef.current) {
        console.log(logPrefix(), "Publishing video track...");
        localVideoTrackRef.current = await participant.publishTrack(videoTrack, {
          simulcast: true,              // enable simulcast layers
          videoEncoding: { maxBitrate: 6_000_000, maxFramerate: 30 },
          source: Track.Source.Camera,
        });
        console.log(logPrefix(), "Video track published:", localVideoTrackRef.current.trackSid);
      }

      // Publish Audio (if needed) and ensure it's unmuted
      let audioInitiallyMuted = true; // Assume muted until confirmed otherwise
      if (audioTrack && !localAudioTrackRef.current) {
        console.log(logPrefix(), "Publishing audio track...");
        localAudioTrackRef.current = await participant.publishTrack(audioTrack, {
          source: Track.Source.Microphone,
        });
        console.log(logPrefix(), "Audio track published:", localAudioTrackRef.current.trackSid);
        try {
          await participant.setMicrophoneEnabled(true); // Attempt to start unmuted
          // --- MODIFIED: Optimistic state setting ---
          audioInitiallyMuted = false; // Optimistically assume success
          console.log(logPrefix(), `Microphone setMicrophoneEnabled(true) called. Optimistically setting state to unmuted.`);
          // --- END MODIFIED ---
        } catch (micEnableError) {
            console.error(logPrefix(), "Error enabling microphone post-publish:", micEnableError);
            audioInitiallyMuted = true; // Fallback to muted on error
        }
      } else if (localAudioTrackRef.current) {
        // If audio track already exists, ensure it's enabled
        if (participant.isMicrophoneMuted) {
          try {
            await participant.setMicrophoneEnabled(true);
            // --- MODIFIED: Optimistic state setting ---
            audioInitiallyMuted = false; // Optimistically assume success
            console.log(logPrefix(), `Existing microphone track setMicrophoneEnabled(true) called. Optimistically setting state to unmuted.`);
            // --- END MODIFIED ---
          } catch (micEnableError) {
              console.error(logPrefix(), "Error enabling existing microphone track:", micEnableError);
              audioInitiallyMuted = true; // Fallback to muted on error
          }
        } else {
          // Already unmuted
          audioInitiallyMuted = false;
        }
      }

      // Set streaming state *after* successful publishing
      setIsStreaming(true); // Indicates camera/mic stream is active
      setStreamUrl(`livekit:${tokenAddress}`);
      const finalCamOff = !participant.isCameraEnabled;
      const finalMicMuted = !!audioInitiallyMuted; // Ensure boolean conversion
      // reset the timer to now
      const liveTime = Date.now();
      setIntroStartTime(liveTime);
      setIsCamOff(finalCamOff);
      setIsMicMuted(finalMicMuted);
      setStreamPhase('live'); // Owner sets phase to live

      // --- ADDED: Detailed log after publishing ---
      console.log(
        logPrefix(),
        `publishLiveTracks COMPLETE. Final SDK States: MicMuted=${participant.isMicrophoneMuted}, CamOff=${!participant.isCameraEnabled}. React States Set: MicMuted=${finalMicMuted}, CamOff=${finalCamOff}`
      );
      // --- END ADDED ---

    } catch (error) {
      console.error(logPrefix(), "Error publishing live stream tracks:", error);
      // Rollback publishing attempts
      if (roomRef.current?.localParticipant) {
        if (localVideoTrackRef.current?.track) await roomRef.current.localParticipant.unpublishTrack(localVideoTrackRef.current.track as LocalTrack).catch(e => console.warn("Unpublish video failed", e));
        if (localAudioTrackRef.current?.track) await roomRef.current.localParticipant.unpublishTrack(localAudioTrackRef.current.track as LocalTrack).catch(e => console.warn("Unpublish audio failed", e));
      }
      localVideoTrackRef.current = null;
      localAudioTrackRef.current = null;
      setIsStreaming(false);
      setStreamUrl(null);
      setStreamPhase('idle'); // Revert phase on error
    }
  }, [tokenAddress, logPrefix]); // Dependencies for publishLiveTracks

  // --- MODIFIED: handleStartStream ---
  const handleStartStream = useCallback(async () => {
    console.log(logPrefix(), "Start Stream clicked (Intro Sequence).");
    if (!isOwnerRef.current || !roomRef.current || isPausedRef.current || streamPhase !== 'idle' || connectionState !== ConnectionState.Connected) {
      console.warn(logPrefix(), "Cannot start stream: conditions not met", { isOwner: isOwnerRef.current, hasRoom: !!roomRef.current, isPaused: isPausedRef.current, streamPhase, connectionState });
      return;
    }

    const banned = await isStreamPaused(ownerAddress!);
    if (banned) {
      setIsPaused(true);
      return;
    }

    if (!localStreamRef.current) {
      console.warn(logPrefix(), "Local preview stream not available. Attempting to get it now.");
      await getLocalPreviewStream();
      if (!localStreamRef.current) {
        console.error(logPrefix(), "Failed to get local stream on demand.");
        alert("Could not access camera/microphone. Please check permissions.");
        return;
      }
    }

    try {
      const startTime = Date.now();
      const data = { type: 'startIntro', startTime };
      const encoder = new TextEncoder();
      console.log(logPrefix(), `Sending 'startIntro' signal with startTime: ${startTime}`);
      await roomRef.current.localParticipant.publishData(encoder.encode(JSON.stringify(data)), DataPacket_Kind.RELIABLE);

      // Update owner's state
      setIntroStartTime(startTime);
      setStreamPhase('intro');
      console.log(logPrefix(), `Intro phase started. Scheduling live stream publish in ${INTRO_DURATION_MS}ms.`);

      // Schedule the actual media publishing after the intro duration
      setTimeout(() => {
        // Check if still in intro phase before publishing (e.g., user didn't click stop)
        if (roomRef.current && streamPhaseRef.current === 'intro') {
             console.log(logPrefix(), "Intro duration elapsed. Calling publishLiveTracks.");
             publishLiveTracks();
        } else {
             console.log(logPrefix(), "Intro duration elapsed, but stream phase is no longer 'intro'. Aborting live publish.");
        }
      }, INTRO_DURATION_MS);

    } catch (error) {
      console.error(logPrefix(), "Error starting intro sequence:", error);
      setStreamPhase('idle'); // Revert phase on error
    }
  }, [connectionState, ownerAddress, getLocalPreviewStream, logPrefix, streamPhase, publishLiveTracks]); // Added streamPhase, publishLiveTracks

  // --- MODIFIED: handleStopStream ---
  const handleStopStream = useCallback(() => {
    console.log(logPrefix(), "Stop Stream clicked / triggered.");
    const participant = roomRef.current?.localParticipant;

    // --- Function to stop local preview stream ---
    const stopLocalPreview = () => {
        if (localStreamRef.current) {
            console.log(logPrefix(), "Stopping existing local preview stream tracks.");
            localStreamRef.current.getTracks().forEach(track => track.stop());
            localStreamRef.current = null;
            setIsPreviewReady(false); // Reset preview state
        }
    };
    // --- ---

    // Unpublish logic remains the same...
    if (participant && (isStreamingRef.current || isSharingScreenRef.current)) {
      console.log(logPrefix(), "Unpublishing all local tracks...");
      const unpublishPromises = Array.from(participant.trackPublications.values()).map(pub => {
        if (pub.track) {
          console.log(logPrefix(), `Attempting to unpublish track: ${pub.kind} (Source: ${pub.source}, SID: ${pub.trackSid})`);
          if (pub.track instanceof LocalTrack) {
            return participant.unpublishTrack(pub.track).catch(unpublishError => {
              console.warn(logPrefix(), `Could not unpublish track ${pub.trackSid}:`, unpublishError);
              return { status: 'rejected', reason: unpublishError };
            });
          } else {
            console.warn(logPrefix(), `Track ${pub.trackSid} is not a LocalTrack, skipping unpublish.`);
            return Promise.resolve({ status: 'fulfilled', value: null });
          }
        }
        return Promise.resolve({ status: 'fulfilled', value: null });
      });

      Promise.allSettled(unpublishPromises).then(results => {
        console.log(logPrefix(), "Finished attempting to unpublish tracks.", results);
        localVideoTrackRef.current = null;
        localAudioTrackRef.current = null;
        screenShareTrackRef.current = null;
        if (screenShareStreamRef.current) {
          console.log(logPrefix(), "Stopping screen share stream tracks.");
          screenShareStreamRef.current.getTracks().forEach(track => track.stop());
          screenShareStreamRef.current = null;
        }
        setIsStreaming(false);
        setIsSharingScreen(false);
        setStreamUrl(null);
        setStreamPhase('idle'); // Ensure phase is reset for owner too
        setIntroStartTime(null);
        console.log(logPrefix(), "Stream stopped (state updated after unpublish attempts).");

        // --- MODIFIED: Stop old preview BEFORE getting new one ---
        if (isOwnerRef.current) {
            stopLocalPreview(); // Stop and clear the old local stream
            console.log(logPrefix(), "Re-enabling local preview after stopping stream.");
            // Use a small delay to ensure state updates settle before getting preview
            setTimeout(() => {
                getLocalPreviewStream();
            }, 100);
        }
        // --- END MODIFIED ---
      });

    } else {
      console.warn(logPrefix(), "Stop stream called but no local participant or not streaming/sharing.");
      // Ensure state is reset even if nothing was published
      setIsStreaming(false);
      setIsSharingScreen(false);
      setStreamUrl(null);
      setStreamPhase('idle');
      setIntroStartTime(null);
      // --- MODIFIED: Stop old preview BEFORE getting new one ---
      if (isOwnerRef.current) {
          stopLocalPreview(); // Stop and clear the old local stream
          console.log(logPrefix(), "Re-enabling local preview after stopping stream (no tracks were published).");
          // Use a small delay
          setTimeout(() => {
              getLocalPreviewStream();
          }, 100);
      }
      // --- END MODIFIED ---
    }

    // Reset phase state immediately for UI responsiveness, even if promises are pending
    setStreamPhase('idle');
    setIntroStartTime(null);

  }, [logPrefix, getLocalPreviewStream]); // Added getLocalPreviewStream dependency

  // --- Stable Mute Handlers ---
  const handleLocalTrackMuted = useCallback((pub: TrackPublication, participant: Participant) => {
    // Uses isOwnerRef internally via logPrefix
    if (participant.isLocal) {
      console.log(logPrefix(), `Local track ${pub.kind} ${pub.isMuted ? 'MUTED' : 'UNMUTED'}`);
      // --- IMPORTANT: Use refs and check against SDK state ---
      if (pub.kind === Track.Kind.Audio) {
          // Only update React state if SDK state differs from ref state
          if (isMicMutedRef.current !== pub.isMuted) {
              console.log(logPrefix(), `handleLocalTrackMuted: Updating isMicMuted state from ${isMicMutedRef.current} to ${pub.isMuted}`);
              setIsMicMuted(pub.isMuted);
          } else {
              console.log(logPrefix(), `handleLocalTrackMuted: SDK audio state ${pub.isMuted} matches ref state. No update needed.`);
          }
      }
      // Only update cam state if not screen sharing
      if (pub.kind === Track.Kind.Video && !isSharingScreenRef.current) {
          if (isCamOffRef.current !== pub.isMuted) {
             console.log(logPrefix(), `handleLocalTrackMuted: Updating isCamOff state from ${isCamOffRef.current} to ${pub.isMuted}`);
             setIsCamOff(pub.isMuted);
          } else {
             console.log(logPrefix(), `handleLocalTrackMuted: SDK video state ${pub.isMuted} matches ref state. No update needed.`);
          }
      }
    }
  }, [logPrefix]); // REMOVED isMicMuted and isCamOff dependencies - NOW STABLE

  const handleRemoteTrackMutedStable = useCallback((pub: TrackPublication, p: Participant) => {
      const currentLogPrefix = isOwnerRef.current ? "[Owner]" : "[Viewer]"; // Use ref
      const currentOwnerAddress = ownerAddress; // Capture ownerAddress from props

      // --- Detailed Logging ---
      console.log(
          currentLogPrefix,
          `>>> Event: Track ${pub.isMuted ? 'MUTED' : 'UNMUTED'} <<<`,
          `Kind: ${pub.kind}, SID: ${pub.trackSid}, Name: ${pub.trackName}`,
          `Participant: ${p.identity} (isLocal: ${p.isLocal})`,
          `OwnerAddress: ${currentOwnerAddress}`
      );
      // --- ---

      if (currentOwnerAddress && p.identity === currentOwnerAddress && !p.isLocal) {
        console.log(currentLogPrefix, `Processing remote owner track ${pub.kind} (${pub.trackSid}) state change: ${pub.isMuted ? 'MUTED' : 'UNMUTED'}`);
        if (pub.kind === Track.Kind.Audio) {
          console.log(currentLogPrefix, `Setting remoteStreamerMicMuted to: ${pub.isMuted}`);
          setRemoteStreamerMicMuted(pub.isMuted); // Update state
        } else if (pub.kind === Track.Kind.Video) {
          console.log(currentLogPrefix, `Setting remoteStreamerCamOff to: ${pub.isMuted}`);
          setRemoteStreamerCamOff(pub.isMuted); // Update state
        }
      } else if (p.isLocal) {
         console.log(currentLogPrefix, `Ignoring local participant mute event in handleRemoteTrackMutedStable.`);
      } else {
         console.log(currentLogPrefix, `Ignoring mute event from non-owner participant: ${p.identity}`);
      }
    }, [ownerAddress]); // Depends on ownerAddress prop to correctly identify the owner

   const setupRemoteStreamerStateStable = useCallback((participant: RemoteParticipant | null) => {
      const currentLogPrefix = isOwnerRef.current ? "[Owner]" : "[Viewer]"; // Use ref
      if (participant) {
        let micMuted = true; // Default to muted
        let camOff = true; // Default to off

        // --- REFINED Audio Check ---
        const audioPub = Array.from(participant.audioTrackPublications.values()).find(pub => pub.isSubscribed && pub.track);
        if (audioPub) {
            micMuted = audioPub.isMuted; // Use the state from the subscribed track's publication
            console.log(currentLogPrefix, `SetupStable: Found subscribed audio pub: SID=${audioPub.trackSid}, isMuted=${micMuted}`);
        } else {
            console.log(currentLogPrefix, `SetupStable: No subscribed audio track found for owner.`);
            // Keep micMuted = true (default)
        }
        // --- END REFINED Audio Check ---

        // --- REFINED Video Check ---
        const videoPub = Array.from(participant.videoTrackPublications.values()).find(pub => pub.isSubscribed && pub.track);
        if (videoPub) {
            camOff = videoPub.isMuted; // Use the state from the subscribed track's publication
             console.log(currentLogPrefix, `SetupStable: Found subscribed video pub: SID=${videoPub.trackSid}, isMuted=${camOff}`);
        } else {
             console.log(currentLogPrefix, `SetupStable: No subscribed video track found for owner.`);
             // Keep camOff = true (default)
        }
        // --- END REFINED Video Check ---


        // --- Log final decision ---
        console.log(currentLogPrefix, `SetupStable: Final decision - Setting remoteMicMuted=${micMuted}, remoteCamOff=${camOff}`);
        // --- ---
        setRemoteStreamerMicMuted(micMuted);
        setRemoteStreamerCamOff(camOff);
      } else {
        console.log(currentLogPrefix, `SetupStable: No remote owner participant found. Resetting state to muted/off.`);
        setRemoteStreamerMicMuted(true); // Default to muted when no participant
        setRemoteStreamerCamOff(true); // Default to off when no participant
      }
    }, []); // Stable, no dependencies

  // --- Stable Participant Count Updater ---
  const updateParticipantCount = useCallback(() => {
      const currentLogPrefix = isOwnerRef.current ? "[Owner]" : "[Viewer]"; // Use ref
      if (roomRef.current) {
        // Count remote participants only
        const count = roomRef.current.remoteParticipants.size;
        setViewerCount(count);
        console.log(currentLogPrefix, "Participant count updated:", count);
      }
    }, []); // Stable

  // --- NEW Stable Callbacks for Listeners used in Main useEffect ---
  const handleTrackSubscribedStable = useCallback((track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
    const currentLogPrefix = isOwnerRef.current ? "[Owner]" : "[Viewer]";
    // Log core event info
    console.log(currentLogPrefix, `Track Subscribed: Kind=${track.kind}, SID=${track.sid}, Participant=${participant.identity}`);

    // Log phase switch decision
    if (
        ownerAddress &&
        participant.identity === ownerAddress &&
        track.kind === Track.Kind.Video &&
        (streamPhaseRef.current === 'intro' || streamPhaseRef.current === 'idle')
    ) {
        const previousPhase = streamPhaseRef.current;
        console.log(currentLogPrefix, `Owner's LIVE VIDEO track subscribed. Switching phase ${previousPhase} -> 'live'.`);
        setStreamPhase('live');
        if (previousPhase === 'intro') { setIntroStartTime(null); }
    }

    // Log state setup trigger
    if (ownerAddress && participant.identity === ownerAddress) {
         console.log(currentLogPrefix, `Owner track (${track.kind}) subscribed. Re-running setupRemoteStreamerStateStable.`);
         setupRemoteStreamerStateStable(participant);
    }
  }, [ownerAddress, setupRemoteStreamerStateStable]);

  const handleDataReceivedStable = useCallback((payload: Uint8Array, participant?: RemoteParticipant, kind?: DataPacket_Kind) => {
    const currentLogPrefix = isOwnerRef.current ? "[Owner]" : "[Viewer]";
    try {
      const decoder = new TextDecoder();
      const message = JSON.parse(decoder.decode(payload));
      // -> peticiones de unión (solo owner)
      if (message.type === 'joinRequest' && isOwnerRef.current && participant) {
        console.log(currentLogPrefix, `Join request recibido de: ${participant.identity}`);
      }
      // -> reacciones (todos) - MEJORADO: log más detallado
      if (message.type === 'reaction') {
        console.log(currentLogPrefix, `Reacción recibida: ${message.emoji} de ${participant?.identity}`, 
                    `- Soy owner: ${isOwnerRef.current}`,
                    `- showBubbles está activo para el componente ReactionOverlay`);
      }
      // ... existente: manejo de startIntro ...
      if (!participant || participant.identity !== ownerAddress) return;
      if (message.type === 'startIntro' && typeof message.startTime === 'number') {
        if (streamPhaseRef.current === 'idle') {
          setIntroStartTime(message.startTime);
          setStreamPhase('intro');
        } else {
          console.log(currentLogPrefix(), `Received 'startIntro' but current phase is '${streamPhaseRef.current}'. Ignoring.`);
        }
      }
    } catch (error) {
      console.error(currentLogPrefix, "Error procesando mensaje DataReceived:", error);
    }
  }, [ownerAddress]);

  const handleParticipantConnectedStable = useCallback((participant: RemoteParticipant) => {
    const currentLogPrefix = isOwnerRef.current ? "[Owner]" : "[Viewer]";
    // Log core event
    console.log(currentLogPrefix, "Participant connected:", participant.identity);
    updateParticipantCount();
    // Log owner specific action
    if (participant.identity === ownerAddress) {
      console.log(currentLogPrefix, "Remote owner connected. Setting up initial state.");
      setupRemoteStreamerStateStable(participant);
    }
  }, [ownerAddress, updateParticipantCount, setupRemoteStreamerStateStable]);

  const handleParticipantDisconnectedStable = useCallback((participant: RemoteParticipant) => {
    const currentLogPrefix = isOwnerRef.current ? "[Owner]" : "[Viewer]";
    // Log core event
    console.log(currentLogPrefix, "Participant disconnected:", participant.identity);
    updateParticipantCount();
    // Log owner specific action
    if (participant.identity === ownerAddress) {
      console.log(currentLogPrefix, "Remote owner disconnected. Resetting remote state.");
      setupRemoteStreamerStateStable(null);
    }
  }, [ownerAddress, updateParticipantCount, setupRemoteStreamerStateStable]);

  const handleConnectionStateChangedStable = useCallback((state: ConnectionState) => {
    const currentLogPrefix = isOwnerRef.current ? "[Owner]" : "[Viewer]";
    // Log core event
    console.log(currentLogPrefix, "Connection State Changed:", state);
    setConnectionState(state);
    // Log specific actions based on state
    if (state === ConnectionState.Connected) {
      updateParticipantCount();
      const remoteOwner = roomRef.current?.getParticipantByIdentity(ownerAddress!);
      if (remoteOwner) {
        console.log(currentLogPrefix, "Connected. Setting up initial remote owner state.");
        setupRemoteStreamerStateStable(remoteOwner);
      } else {
        console.log(currentLogPrefix, "Connected, but remote owner not found yet.");
        setupRemoteStreamerStateStable(null);
      }
      // NO llamar automáticamente a getLocalPreviewStream para el propietario
      // if (isOwnerRef.current) {
      //   console.log(currentLogPrefix, "Owner connected, starting local preview.");
      //   getLocalPreviewStream();
      // }
    }
    if (state === ConnectionState.Disconnected) {
      console.log(currentLogPrefix, "Disconnected. Resetting stream states.");
      // Reset relevant states
      setStreamPhase('idle');
      setIntroStartTime(null);
      setIsStreaming(false);
      setIsSharingScreen(false);
      setStreamUrl(null);
      setViewerCount(0);
      // ... any other states needing reset ...
      setupRemoteStreamerStateStable(null); // Ensure remote state is reset
    }
  }, [ownerAddress, updateParticipantCount, setupRemoteStreamerStateStable, getLocalPreviewStream]); // Depends on ownerAddress and stable callbacks
  // --- ---

  // --- NEW Stable Callback for Track Unpublished ---
  const handleTrackUnpublishedStable = useCallback((publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      const currentLogPrefix = isOwnerRef.current ? "[Owner]" : "[Viewer]";
      // Log core event
      console.log(currentLogPrefix, `Track Unpublished: Kind=${publication.kind}, SID=${publication.trackSid}, Source=${publication.source}, Participant=${participant.identity}`);

      // Log phase reset decision
      if (!isOwnerRef.current && ownerAddress && participant.identity === ownerAddress && publication.kind === Track.Kind.Video) {
          const remainingVideoPubs = Array.from(participant.videoTrackPublications.values())
                                        .filter(pub => pub.trackSid !== publication.trackSid && (pub.isSubscribed || pub.track));
          if (remainingVideoPubs.length === 0) {
              console.log(currentLogPrefix, "Owner's last video track unpublished. Resetting stream phase to 'idle'.");
              setStreamPhase('idle');
              setIntroStartTime(null);
          }
      }
  }, [ownerAddress]);
  // --- ---

  // NUEVO: Función para manejar la solicitud de previsualización
  const handleRequestPreview = useCallback(() => {
    console.log(logPrefix(), "Preview requested by owner");
    setShowPreview(true);
    // Solicitar permisos y conseguir el stream
    getLocalPreviewStream();
  }, [logPrefix, getLocalPreviewStream]);

  // NUEVO: Función para detener la previsualización
  const handleStopPreview = useCallback(() => {
    console.log(logPrefix(), "Stop Preview requested by owner");
    
    // Detener la transmisión local si existe
    if (localStreamRef.current) {
      console.log(logPrefix(), "Stopping local preview stream tracks");
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    // Resetear estados relacionados con la previsualización
    setShowPreview(false);
    setIsPreviewReady(false);
    
  }, [logPrefix]);

  // Modified useEffect for getting local preview
  useEffect(() => {
    // Log when this effect runs and the state value that triggered it
    console.log(logPrefix(), `Preview useEffect triggered. isOwner constant: ${isOwner}`);
    
    // No obtener la previsualización automáticamente, ahora es controlado por showPreview
    if (!isOwner) { // Solo reset si no es owner
      // Reset preview state if user is no longer owner
      setIsPreviewReady(false);
      setShowPreview(false); // <-- NUEVO: También resetear si debe mostrarse
      if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(track => track.stop());
          localStreamRef.current = null;
      }
    }
    // No cleanup needed here unless getLocalPreviewStream returns one
  }, [isOwner, getLocalPreviewStream]); // Depend on isOwner state and the stable callback

  // Dedicated useEffect for Ban Checking Interval
  useEffect(() => {
    const interval = setInterval(async () => {
      if (ownerAddress) {
        const banned = await isStreamPaused(ownerAddress);
        if (banned && (isStreamingRef.current || isSharingScreenRef.current)) {
          console.log(logPrefix(), "Stream detected as paused/banned via interval, stopping stream.");
          setIsPaused(true);
          handleStopStream();
        } else if (!banned && isPausedRef.current) {
          console.log(logPrefix(), "Stream no longer banned via interval.");
          setIsPaused(false);
        }
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [ownerAddress, setIsPaused, handleStopStream, logPrefix]); // Removed isStreaming, isSharingScreen, isPaused

  // Dedicated useEffect for initial Ban Check on ownerAddress change
  useEffect(() => {
    const checkBanStatus = async () => {
      if (!ownerAddress) return;
      const ownerStatus = connectedWallet && ownerAddress ? connectedWallet.toLowerCase() === ownerAddress.toLowerCase() : false;
      const currentLogPrefix = ownerStatus ? "[Owner]" : "[Viewer]";
      console.log(currentLogPrefix, "Running initial ban check...");
      const banned = await isStreamPaused(ownerAddress);
      setIsPaused(banned);
      if (banned) {
        console.log(currentLogPrefix, "Initial check found stream is banned.");
      } else {
        console.log(currentLogPrefix, "Initial check found stream is not banned.");
      }
    };
    checkBanStatus();
  }, [ownerAddress, connectedWallet, setIsPaused, logPrefix]); // Removed isOwner state dependency

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reportsRes] = await Promise.all([
          axios.get(`${apiUrl}/api/getReports`),
        ]);
        for (let i = 0; i < reportsRes.data.length; i++) {
          if (reportsRes.data[i].reporter === address) {
            setReported(true);
            return;
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    if (address) {
      fetchData();
    }
  }, [address]);

  // --- Main Connection useEffect (Revised) ---
  useEffect(() => {
    if (roomRef.current instanceof Room) {
      console.log("Main Effect: Disconnecting previous room instance in setup");
      roomRef.current.disconnect();
      roomRef.current = null;
    }

    if (!tokenAddress || !ownerAddress) {
      console.log("Main Effect: Token address or owner address undefined, awaiting valid props");
      setStreamUrl(null);
      setConnectionState(ConnectionState.Disconnected);
      return;
    }

    const currentLogPrefix = isOwner ? "[Owner]" : "[Viewer]";
    console.log(currentLogPrefix, "Main Effect: Props updated/mounted. Owner status (from constant):", isOwner);

    console.log(currentLogPrefix, "Main Effect: Creating new Room instance.");
    const room = new Room({
      adaptiveStream: true,              // CAMBIO: Activar para permitir adaptación automática a condiciones de red
      dynacast: true,                    // CAMBIO: Activar para optimizar uso de ancho de banda
      videoCaptureDefaults: { resolution: VideoPresets.h720 }, // CAMBIO: Reducir a 720p para mayor fluidez
      publishDefaults: {
        videoSimulcastLayers: [VideoPresets.h540, VideoPresets.h720], // CAMBIO: Añadir capa de 540p
        videoCodec: 'vp8',
        dtx: true,                       // CAMBIO: Activar transmisión discontinua para ahorro de ancho de banda
        red: true,                       // CAMBIO: Activar redundancia para mejor calidad
        forceStereo: false,              // CAMBIO: Desactivar stereo para ahorrar ancho de banda
        videoEncoding: {
          maxBitrate: 2_500_000,         // CAMBIO: Reducir a 2.5 Mbps para mejor estabilidad
          maxFramerate: 30
        }
      },
      rtcConfig: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          // Añadir servidores TURN si es posible para mejorar la conectividad
        ]
      }
    });
    roomRef.current = room;
    const currentRoom = roomRef.current;

    // --- Attach Stable Listeners ---
    room.on(RoomEvent.TrackMuted, handleRemoteTrackMutedStable);
    room.on(RoomEvent.TrackUnmuted, handleRemoteTrackMutedStable);
    room.on(RoomEvent.TrackMuted, handleLocalTrackMuted);
    room.on(RoomEvent.TrackUnmuted, handleLocalTrackMuted);
    room.on(RoomEvent.ParticipantConnected, handleParticipantConnectedStable); // Use stable callback
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnectedStable); // Use stable callback
    room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChangedStable); // Use stable callback
    room.on(RoomEvent.TrackSubscribed, handleTrackSubscribedStable); // Use stable callback
    room.on(RoomEvent.DataReceived, handleDataReceivedStable); // Use stable callback
    room.on(RoomEvent.TrackUnpublished, handleTrackUnpublishedStable); // Add the new listener
    // --- ---

    const connectToRoom = async () => {
      let connectionToken: string | null = null;
      try {
        if (!tokenAddress || (!connectedWallet && !isOwner)) {
          console.error(currentLogPrefix, "Missing required data for LiveKit connection (tokenAddress or connectedWallet).");
          return;
        }
        const identity = isOwner ? connectedWallet! : (connectedWallet || `visitor-${Date.now()}`);
        console.log(currentLogPrefix, `Fetching LiveKit token for identity: ${identity}, tokenAddress: ${tokenAddress}, isOwner: ${isOwner}`);

        const response = await fetch(`${serverConfig.serverUrl}/livekit-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenAddress, identity, isOwner: isOwner }),
        });
        if (!response.ok) throw new Error(`Token request failed: ${await response.text()}`);
        const data = await response.json();
        connectionToken = data.token;
        console.log(currentLogPrefix, "Token received. Attempting to connect to LiveKit...");

        setConnectionState(ConnectionState.Connecting);
        console.log(currentLogPrefix, `Calling room.connect with URL: ${serverConfig.livekitWsUrl}`);
        await room.connect(serverConfig.livekitWsUrl, connectionToken, { autoSubscribe: true });
        console.log(currentLogPrefix, "room.connect promise resolved successfully for room:", tokenAddress);

      } catch (error: any) {
        console.error(currentLogPrefix, "Error during LiveKit connection process:", error);
        if (error.name === 'ConnectionError') {
          console.error(currentLogPrefix, `ConnectionError details: code=${error.code}, message=${error.message}`);
        }
        console.error(currentLogPrefix, `Connection failed. Token was ${connectionToken ? 'obtained' : 'NOT obtained'}.`);
        setConnectionState(ConnectionState.Disconnected);
        if (roomRef.current === room) {
          roomRef.current = null;
        }
      }
    };

    if (tokenAddress && (isOwner || connectedWallet)) {
      connectToRoom();
    } else {
      console.warn(currentLogPrefix, "Skipping connection: Missing tokenAddress or connectedWallet (for viewer).");
    }

    return () => {
      console.log(logPrefix(), "Main Effect Cleanup: Disconnecting room and stopping streams.");
      if (currentRoom instanceof Room) {
        // --- Detach Stable Listeners ---
        currentRoom.off(RoomEvent.TrackMuted, handleRemoteTrackMutedStable);
        currentRoom.off(RoomEvent.TrackUnmuted, handleRemoteTrackMutedStable);
        currentRoom.off(RoomEvent.TrackMuted, handleLocalTrackMuted);
        currentRoom.off(RoomEvent.TrackUnmuted, handleLocalTrackMuted);
        currentRoom.off(RoomEvent.TrackSubscribed, handleTrackSubscribedStable); // Remove listener added above
        currentRoom.off(RoomEvent.DataReceived, handleDataReceivedStable); // Remove listener added above
        currentRoom.off(RoomEvent.ParticipantConnected, handleParticipantConnectedStable); // Remove listener added above
        currentRoom.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnectedStable); // Remove listener added above
        currentRoom.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChangedStable); // Remove listener added above
        currentRoom.off(RoomEvent.TrackUnpublished, handleTrackUnpublishedStable); // Remove the new listener
        // Remove other listeners added directly in this effect too
        // currentRoom.off(RoomEvent.ParticipantConnected, ...); // Need the exact function reference or make them stable too
        // currentRoom.off(RoomEvent.ParticipantDisconnected, ...);
        // currentRoom.off(RoomEvent.ConnectionStateChanged, ...);
        // ...
        currentRoom.disconnect();
   
      }
      if (localStreamRef.current) {
        console.log("Stopping local preview stream on unmount");
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }
      if (screenShareStreamRef.current) {
        screenShareStreamRef.current.getTracks().forEach(track => track.stop());
        screenShareStreamRef.current = null;
      }
      setConnectionState(ConnectionState.Disconnected);
      setIsStreaming(false);
      setStreamUrl(null);
      setViewerCount(0);
      setIsMicMuted(false);
      setIsCamOff(false);
      setIsSharingScreen(false);
      localVideoTrackRef.current = null;
      localAudioTrackRef.current = null;
      screenShareTrackRef.current = null;
      setIsPreviewReady(false); // Reset preview state on full cleanup
      setStreamPhase('idle');
      setIntroStartTime(null);
    };
    // --- Update Dependencies ---
  }, [
      tokenAddress,
      connectedWallet,
      ownerAddress, // ownerAddress is needed by handleRemoteTrackMutedStable
      address,
      logPrefix,
      isOwner,
      // Stable callbacks:
      handleLocalTrackMuted, // NOW STABLE
      handleRemoteTrackMutedStable, // Stable
      setupRemoteStreamerStateStable, // Stable
      updateParticipantCount, // Stable
      // Add publishLiveTracks as it's used in the effect's scope (though indirectly via handleStartStream dependency)
      publishLiveTracks,
      // Add NEW stable listener callbacks to dependency array
      handleTrackSubscribedStable,
      handleDataReceivedStable,
      handleParticipantConnectedStable,
      handleParticipantDisconnectedStable,
      handleConnectionStateChangedStable,
      handleTrackUnpublishedStable // Add the new stable callback
  ]);
  // --- ---

  // --- NEW useEffect to handle republishing camera after screen share stops ---
  useEffect(() => {
      const wasSharing = prevIsSharingScreenRef.current;
      const isNowSharing = isSharingScreen; // Current state value from props/state

      // Check if we *just stopped* sharing (transitioned from true to false)
      if (wasSharing === true && isNowSharing === false && isOwnerRef.current) {
          console.log(logPrefix(), "[useEffect isSharingScreen] Detected screen share stopped. Attempting to republish camera.");
          // Ensure the video track isn't already published somehow (double check)
          // Use a small delay to allow SDK state to potentially settle after onended unpublish
          setTimeout(() => {
              if (!localVideoTrackRef.current) {
                  republishCamera(); // Call the stable republish function
              } else {
                  console.log(logPrefix(), "[useEffect isSharingScreen] Screen share stopped, but camera track ref already exists. Skipping republish.");
                  // If track exists but might be disabled, ensure it's enabled
                  const participant = roomRef.current?.localParticipant;
                  if (participant && !participant.isCameraEnabled) {
                      console.log(logPrefix(), "[useEffect isSharingScreen] Enabling existing camera track.");
                      participant.setCameraEnabled(true).then(() => setIsCamOff(false)).catch(e => console.error("Error enabling camera:", e));
                  } else if (participant?.isCameraEnabled) {
                      setIsCamOff(false); // Ensure state matches SDK if already enabled
                  }
              }
          }, 100); // Added 100ms delay
      }

      // Update previous value ref for the next render cycle
      prevIsSharingScreenRef.current = isNowSharing;
  }, [isSharingScreen, republishCamera, logPrefix]); // Depend on isSharingScreen state and stable callbacks
  // --- ---

  useImperativeHandle(ref, () => ({ room: roomRef.current }), []);

  if (!tokenAddress) {
    return <div>Loading stream data...</div>;
  }

  const getStatusText = () => {
    switch (connectionState) {
      case ConnectionState.Connecting: return "Connecting...";
      case ConnectionState.Connected: return "Connected";
      case ConnectionState.Reconnecting: return "Reconnecting...";
      case ConnectionState.Disconnected: return "Disconnected";
      default: return "Unknown";
    }
  };

  // --- Revised Player Visibility Logic ---
  const showOwnerPreview = isOwner && isPreviewReady && localStreamRef.current;
  const remoteOwnerParticipant = ownerAddress ? roomRef.current?.getParticipantByIdentity(ownerAddress) : null;
  // Check if owner has *any* track published (could be data during intro, or media during live)
  const isRemoteOwnerPublishingAnything = remoteOwnerParticipant ? remoteOwnerParticipant.trackPublications.size > 0 : false;

  // Viewer should see the player if connected and the stream phase is 'live'
  const showRemoteStreamLive = !isOwner && connectionState === ConnectionState.Connected && streamPhase === 'live';
  // Viewer should see the intro if connected and the stream phase is 'intro'
  const showRemoteStreamIntro = !isOwner && connectionState === ConnectionState.Connected && streamPhase === 'intro';

  // Determine what element to show
  const showIntroElement = !isPaused && (showRemoteStreamIntro);
  const showLivePlayerElement = !isPaused && (showOwnerPreview || showRemoteStreamLive);
  // --- ---

  // Determine if *any* local media track is published for controls/status text
  const isLive = isStreaming || isSharingScreen; // This remains based on actual media publishing state

  // --- Log conditions before rendering ---
  console.log(`[Render Check] isOwner=${isOwner}, isPaused=${isPaused}, connectionState=${connectionState}, streamPhase=${streamPhase}, isPreviewReady=${isPreviewReady}, isRemoteOwnerPublishingAnything=${isRemoteOwnerPublishingAnything}, showIntroElement=${showIntroElement}, showLivePlayerElement=${showLivePlayerElement}`);
  const shouldShowControls = isOwner && ownerAddress && !isPaused && connectionState === ConnectionState.Connected;
  // console.log(`[Render Check] Should Show Controls: ${shouldShowControls}`); // Removed this derived log
  // --- ---

  // before return:
  // determine which streams to render for viewers (only owner)
  const remoteStreams = (
    !isOwner &&
    connectionState === ConnectionState.Connected &&
    streamPhase === 'live' &&
    ownerAddress &&
    roomRef.current
  )
    ? [ownerAddress]
    : [];
  const ownerLiveStartTime = introStartTime;

  return (
    <div className="relative mx-auto stream-container-wrapper"> {/* Añadir 'relative' si ChatOverlay usa 'absolute' */}

      {/* Status line */}
      <div className="flex items-center justify-between mb-2 text-sm text-gray-400">
        <span>Status: {getStatusText()} {connectionState === ConnectionState.Connecting && <FaSpinner className="inline ml-1 animate-spin" />}</span>
        {connectionState === ConnectionState.Connected && (
          <span>
            {isLive ? 'Viewers' : 'Waiting'}: {viewerCount}
          </span>
        )}
      </div>

      {/* spectator panel with reaction buttons (only in live mode) */}
      {showRemoteStreamLive && (
        <div className="flex gap-2 mb-4 spectator-panel">
          {!reported && (
            <button
              onClick={() => setShowReportModal(true)}
              className="px-3 py-1 text-white bg-red-600 rounded"
            >
              Report Stream
            </button>
          )}
          <JoinRequestButton
            room={roomRef.current!}
            isOwner={false}
            onAccept={id => console.log("Join accepted for", id)}
          />
          {/* Reaction buttons for viewer */}
          <ReactionOverlay
            room={roomRef.current!}
            showButtons={true}
            showBubbles={false}
          />
        </div>
      )}

      {/* Single ReactionOverlay for the owner that shows bubbles but not buttons */}
      {isOwner && roomRef.current && connectionState === ConnectionState.Connected && (
        <div className="reaction-container">
          <ReactionOverlay
            room={roomRef.current}
            showButtons={false}
            showBubbles={true}
          />
        </div>
      )}

      {/* owner controls - MODIFICADO para mostrar botón de Preview y Stop Preview */}
      {shouldShowControls && (
        streamPhase === 'idle' ? (
          // En estado inicial, mostrar botones de Start Stream y Preview/Stop Preview
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={handleStartStream}
              disabled={isLive} 
              className={`p-2 rounded-[10px] text-white flex-grow ${
                isLive
                  ? 'bg-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#1c5290] to-[#45c0be] hover:opacity-90'
              }`}
            >
              Start Stream
            </button>
            {showPreview ? (
              // Mostrar botón "Stop Preview" cuando la previsualización está activa
              <button
                onClick={handleStopPreview}
                className="p-2 rounded-[10px] text-white flex-grow bg-red-600 hover:bg-red-700"
              >
                Stop Preview
              </button>
            ) : (
              // Mostrar botón "Preview" cuando la previsualización no está activa
              <button
                onClick={handleRequestPreview}
                className="p-2 rounded-[10px] text-white flex-grow bg-gradient-to-r from-[#1c5290] to-[#45c0be] hover:opacity-90"
              >
                Preview
              </button>
            )}
          </div>
        ) : (
          // Usar el StreamControls normal para otros estados
          <StreamControls
            onStart={handleStartStream}
            onStop={handleStopStream}
            isStreaming={isLive || streamPhase === 'intro'}
            isOwner={isOwner}
            isMicMuted={isMicMuted}
            isCamOff={isCamOff}
            isSharingScreen={isSharingScreen}
            onToggleMic={toggleMic}
            onToggleCam={toggleCam}
            onStartScreenShare={startScreenShare}
            onStopScreenShare={stopScreenShare}
            disableMediaControls={streamPhase === 'intro'}
          />
        )
      )}

      {/* main content: intro, owner preview, or viewer stream - MODIFICADO para considerar showPreview */}
      {showIntroElement ? (
        <IntroPlayer
          src={INTRO_VIDEO_SRC}
          startTime={introStartTime}
          logPrefix={logPrefix()}
        />
      ) : showLivePlayerElement ? (
        isOwner ? (
          <StreamPlayer
            streamUrl={streamUrl}
            room={roomRef.current}
            isOwner={true}
            localStream={localStreamRef.current}
            viewerCount={viewerCount}
            logPrefix={logPrefix()}
            remoteMicMuted={false}
            remoteCamOff={false}
            ownerAddress={ownerAddress}
            liveStartTime={introStartTime}
            isMicMuted={isMicMuted} // Pass the isMicMuted state for owner
          />
        ) : (
          <StreamPlayer
            streamUrl={streamUrl}
            room={roomRef.current}
            isOwner={isOwner}
            localStream={localStreamRef.current}
            viewerCount={viewerCount}
            logPrefix={logPrefix()}
            remoteMicMuted={remoteStreamerMicMuted}
            remoteCamOff={remoteStreamerCamOff}
            ownerAddress={ownerAddress!}
            liveStartTime={ownerLiveStartTime}
          />
        )
      ) : (
        !isPaused && (
          connectionState === ConnectionState.Connecting ?
            <div className="bg-[#050f1a] p-4 rounded-[10px] text-white w-full mt-4 flex items-center justify-center">
              <FaSpinner className="mr-2 animate-spin" /> Connecting to stream...
            </div>
          // MODIFICADO: Mostrar la previsualización solo si se ha solicitado
          : isOwner && connectionState === ConnectionState.Connected && showPreview && !isPreviewReady ?
            <div className="bg-[#050f1a] p-4 rounded-[10px] text-white w-full mt-4">Enable camera/mic permissions for preview...</div>
          // MODIFICADO: Mensaje cuando está conectado pero no se ha solicitado la previsualización
          : isOwner && connectionState === ConnectionState.Connected && !showPreview ?
            <div className="bg-[#050f1a] p-4 rounded-[10px] text-white w-full mt-4 flex flex-col items-center justify-center min-h-[200px]">
              <h3 className="text-xl font-bold mb-2">Click "Preview" to check your camera</h3>
              <p className="text-gray-400 text-center">
                You'll be able to adjust your camera and microphone before going live.
              </p>
            </div>
          : !isOwner && connectionState === ConnectionState.Connected && streamPhase === 'idle' ?
            // ENHANCED WAITING STATE for viewers
            <div className="bg-[#050f1a] p-8 rounded-[10px] text-white w-full mt-4 flex flex-col items-center justify-center min-h-[300px]">
              <div className="relative mb-6">
                <FaBroadcastTower className="text-5xl text-[#45c0be]" />
                <div className="absolute inset-0 bg-[#45c0be] rounded-full opacity-20 animate-ping"></div>
              </div>
              <h3 className="text-2xl font-bold mb-2">Waiting for Stream to Start</h3>
              <p className="text-gray-400 text-center mb-4">
                The broadcaster hasn't started streaming yet.
                <br />Stay tuned, the stream should begin soon!
              </p>
              {ownerAddress && (
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <FaUser className="mr-2" />
                  <span>Broadcaster: {ownerAddress.slice(0, 6)}...{ownerAddress.slice(-4)}</span>
                </div>
              )}
              <div className="flex items-center text-sm text-gray-500">
                <FaCalendarAlt className="mr-2" />
                <span>Connected {new Date().toLocaleTimeString()}</span>
              </div>
            </div>
          : connectionState === ConnectionState.Disconnected && !isOwner ?
            <div className="bg-[#050f1a] p-4 rounded-[10px] text-white w-full mt-4 flex flex-col items-center justify-center min-h-[200px]">
              <div className="text-2xl text-red-500 mb-3">Stream Offline</div>
              <p className="text-gray-400 text-center">
                The stream is currently unavailable or has ended.
                <br />Please check back later.
              </p>
            </div>
          : null
        )
      )}

      {/* Renderizar ChatOverlay si includeChat es true y estamos conectados */}
      {includeChat && roomRef.current && connectionState === ConnectionState.Connected && (
         <div className="chat-section"> {/* Usar la clase del CSS */}
            <ChatOverlay room={roomRef.current} />
         </div>
      )}

    </div>
  );
});

export default StreamContainer;